import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { 
  User, Briefcase, Mail, Phone, Globe, MapPin,
  Linkedin, Twitter, Instagram, Youtube, Facebook, Github,
  Camera, Upload, ChevronRight, ChevronLeft, ChevronUp, Check, 
  Download, Share2, Save, Eye, X, Building, MessageCircle, Palette, Plus,
  Music, Twitch, Podcast, Link2, TikTokIcon, Slack, Discord, CreditCard, Crop
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../contexts/AuthContext';
import SignInModal from '../components/SignInModal';
import ImageCropper from '../components/ImageCropper';
import SEO from '../components/SEO';
import { compressImage } from '../utils/imageCompression';
import { COUNTRY_CODES, DEFAULT_COUNTRY_CODE, parsePhoneNumber } from '../utils/countryCodes';
import CountryCodeSelect from '../components/CountryCodeSelect';
import BioTemplateRenderer from '../components/bio-templates/BioTemplateRenderer';
import QRCodeWithLogo from '../components/QRCodeWithLogo';
import RichTextEditor from '../components/RichTextEditor';
import { FileText } from 'lucide-react';
import { uploadImage as firebaseUploadImage } from '../firebase/config';

// TikTok icon (not in lucide-react)
const TikTok = ({ className, style }) => (
  <svg className={className} style={style} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

// Snapchat icon
const Snapchat = ({ className, style }) => (
  <svg className={className} style={style} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12.206.793c.99 0 4.347.276 5.93 3.821.529 1.193.403 3.219.299 4.847l-.003.06c-.012.18-.022.345-.03.51.075.045.203.09.401.09.3-.016.659-.12 1.033-.301.165-.088.344-.104.465-.104.135 0 .27.018.39.055.27.075.49.252.539.569.054.327-.16.627-.44.848-.39.301-1.08.509-1.56.569-.09.015-.181.015-.27.03-.18.015-.36.045-.45.179-.12.165-.135.315-.12.465l.015.135c.12.555.36 1.156.66 1.681.3.54.66 1.02 1.02 1.426a4.66 4.66 0 0 0 1.35 1.12c.21.12.405.24.585.359.27.165.42.42.45.72.045.48-.27.899-.705 1.065a4.464 4.464 0 0 1-1.035.21 8.544 8.544 0 0 1-1.17 0c-.42 0-.81.015-1.17.074-.24.045-.48.09-.72.15-.33.075-.66.195-.96.389-.39.255-.78.645-1.38.855-.18.06-.39.09-.615.09-.21 0-.42-.03-.615-.09-.6-.21-.99-.6-1.38-.855-.3-.194-.63-.314-.96-.389-.24-.06-.48-.105-.72-.15-.36-.059-.75-.074-1.17-.074-.39.015-.78-.015-1.17 0-.36-.03-.705-.105-1.035-.21-.435-.166-.75-.585-.705-1.065.03-.3.18-.555.45-.72.18-.119.375-.239.585-.359a4.66 4.66 0 0 0 1.35-1.12c.36-.406.72-.886 1.02-1.426.3-.525.54-1.126.66-1.681l.015-.135c.015-.15 0-.3-.12-.465-.09-.134-.27-.164-.45-.179-.09-.015-.18-.015-.27-.03-.48-.06-1.17-.268-1.56-.569-.28-.221-.494-.521-.44-.848.05-.317.269-.494.54-.569.119-.037.254-.055.389-.055.12 0 .3.016.465.104.374.181.733.285 1.033.301.198 0 .326-.045.401-.09-.008-.165-.018-.33-.03-.51l-.003-.06c-.104-1.628-.23-3.654.299-4.847C7.859 1.069 11.216.793 12.206.793z"/>
  </svg>
);

// Pinterest icon  
const Pinterest = ({ className, style }) => (
  <svg className={className} style={style} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 0a12 12 0 0 0-4.373 23.178c-.07-.937-.134-2.377.028-3.401.146-.927.943-3.996.943-3.996s-.24-.481-.24-1.192c0-1.117.647-1.951 1.453-1.951.686 0 1.017.515 1.017 1.132 0 .69-.44 1.722-.666 2.678-.19.8.401 1.453 1.19 1.453 1.428 0 2.526-1.506 2.526-3.678 0-1.923-1.382-3.267-3.355-3.267-2.286 0-3.628 1.714-3.628 3.486 0 .69.266 1.43.598 1.833a.24.24 0 0 1 .056.23c-.061.254-.197.8-.224.912-.035.146-.116.178-.268.107-1-.465-1.624-1.926-1.624-3.1 0-2.523 1.834-4.84 5.286-4.84 2.775 0 4.932 1.977 4.932 4.62 0 2.757-1.739 4.976-4.151 4.976-.811 0-1.573-.421-1.834-.919l-.498 1.902c-.181.695-.669 1.566-.995 2.097A12 12 0 1 0 12 0z"/>
  </svg>
);

const STEPS = [
  { id: 1, title: 'Basic Info', icon: User },
  { id: 2, title: 'Bio', icon: FileText },
  { id: 3, title: 'Photo & Logo', icon: Camera },
  { id: 4, title: 'Contact', icon: Phone },
  { id: 5, title: 'Social Links', icon: Globe },
  { id: 6, title: 'Gallery', icon: Camera }
];

// Available themes
const THEMES = [
  { id: 'modern', name: 'Modern', preview: 'bg-gradient-to-br from-gray-900 to-gray-800', textColor: 'text-white' },
  { id: 'classic', name: 'Classic', preview: 'bg-white border-2 border-gray-200', textColor: 'text-gray-900' },
  { id: 'gradient', name: 'Gradient', preview: 'bg-gradient-to-br from-purple-600 to-blue-500', textColor: 'text-white' },
  { id: 'minimal', name: 'Minimal', preview: 'bg-gray-50 border border-gray-100', textColor: 'text-gray-800' },
  { id: 'professional', name: 'Professional', preview: 'bg-slate-900', textColor: 'text-white' },
  { id: 'executive', name: 'Executive', preview: 'bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900', textColor: 'text-white' },
  { id: 'creative', name: 'Creative', preview: 'bg-gradient-to-br from-pink-500 via-purple-500 to-indigo-500', textColor: 'text-white' }
];

const CardBuilder = () => {
  const navigate = useNavigate();
  const { username } = useParams(); // Get username from URL for edit mode
  const { toast } = useToast();
  const { user } = useAuth();
  
  const [currentStep, setCurrentStep] = useState(1);
  const [showPreview, setShowPreview] = useState(false);
  const [showSignInModal, setShowSignInModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedTheme, setSelectedTheme] = useState('modern'); // Default to Modern
  const [pendingSave, setPendingSave] = useState(false); // Track if save was attempted before sign-in
  const [isEditMode, setIsEditMode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [existingUsername, setExistingUsername] = useState(null);
  
  // Swipe gesture state
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [touchStartX, setTouchStartX] = useState(null);
  const [touchEndX, setTouchEndX] = useState(null);
  const minSwipeDistance = 50; // Minimum swipe distance in pixels
  
  // Gallery state
  const [galleryImages, setGalleryImages] = useState([]);
  const [youtubeLinks, setYoutubeLinks] = useState([]);
  const galleryInputRef = useRef(null);
  
  // Available social platforms (expanded list)
  const SOCIAL_PLATFORMS = [
    { id: 'linkedin', name: 'LinkedIn', icon: Linkedin, color: '#0077B5', placeholder: 'username', urlPattern: /(?:linkedin\.com\/in\/|linkedin\.com\/company\/)?([^\/\?]+)/ },
    { id: 'twitter', name: 'Twitter / X', icon: Twitter, color: '#000000', placeholder: 'username', urlPattern: /(?:twitter\.com\/|x\.com\/)?@?([^\/\?]+)/ },
    { id: 'instagram', name: 'Instagram', icon: Instagram, color: '#E4405F', placeholder: 'username', urlPattern: /(?:instagram\.com\/)?@?([^\/\?]+)/ },
    { id: 'youtube', name: 'YouTube', icon: Youtube, color: '#FF0000', placeholder: '@channel or channel ID', urlPattern: /(?:youtube\.com\/@|youtube\.com\/channel\/|youtube\.com\/c\/)?@?([^\/\?]+)/ },
    { id: 'facebook', name: 'Facebook', icon: Facebook, color: '#1877F2', placeholder: 'username or page', urlPattern: /(?:facebook\.com\/)?([^\/\?]+)/ },
    { id: 'github', name: 'GitHub', icon: Github, color: '#333333', placeholder: 'username', urlPattern: /(?:github\.com\/)?([^\/\?]+)/ },
    { id: 'whatsapp', name: 'WhatsApp', icon: MessageCircle, color: '#25D366', placeholder: 'phone number', urlPattern: null },
    { id: 'tiktok', name: 'TikTok', icon: TikTok, color: '#000000', placeholder: 'username', urlPattern: /(?:tiktok\.com\/@)?@?([^\/\?]+)/ },
    { id: 'snapchat', name: 'Snapchat', icon: Snapchat, color: '#FFFC00', placeholder: 'username', urlPattern: /(?:snapchat\.com\/add\/)?([^\/\?]+)/ },
    { id: 'pinterest', name: 'Pinterest', icon: Pinterest, color: '#E60023', placeholder: 'username', urlPattern: /(?:pinterest\.com\/)?([^\/\?]+)/ },
    { id: 'spotify', name: 'Spotify', icon: Music, color: '#1DB954', placeholder: 'artist/playlist ID', urlPattern: /(?:open\.spotify\.com\/(?:artist|user|playlist)\/)?([^\/\?]+)/ },
    { id: 'discord', name: 'Discord', icon: MessageCircle, color: '#5865F2', placeholder: 'invite code', urlPattern: /(?:discord\.gg\/|discord\.com\/invite\/)?([^\/\?]+)/ },
    { id: 'telegram', name: 'Telegram', icon: MessageCircle, color: '#26A5E4', placeholder: 'username', urlPattern: /(?:t\.me\/)?@?([^\/\?]+)/ },
    { id: 'threads', name: 'Threads', icon: MessageCircle, color: '#000000', placeholder: 'username', urlPattern: /(?:threads\.net\/@)?@?([^\/\?]+)/ },
  ];
  
  // Website domain extensions
  const DOMAIN_EXTENSIONS = ['.com', '.in', '.org', '.net', '.io', '.co', '.ai', '.app', '.dev', '.tech', '.me', '.info', '.biz', '.us', '.uk', '.ca', '.au', '.de', '.fr', '.xyz'];
  
  // Helper function to extract handle from URL
  const extractHandle = (value, platform) => {
    if (!value) return '';
    
    // Remove protocol and www
    let cleaned = value.trim()
      .replace(/^https?:\/\//, '')
      .replace(/^www\./, '');
    
    // If platform has a URL pattern, try to extract handle
    if (platform?.urlPattern) {
      const match = cleaned.match(platform.urlPattern);
      if (match && match[1]) {
        return match[1].replace(/^@/, ''); // Remove @ prefix if present
      }
    }
    
    // If it looks like a URL but no pattern matched, try generic extraction
    if (cleaned.includes('/')) {
      const parts = cleaned.split('/').filter(p => p);
      // Get the last meaningful part
      return parts[parts.length - 1] || parts[0] || cleaned;
    }
    
    // Return cleaned value without @ prefix
    return cleaned.replace(/^@/, '');
  };
  
  // Helper function to extract YouTube video ID from URL or return as-is if already an ID
  const extractYouTubeId = (input) => {
    if (!input) return '';
    
    const trimmed = input.trim();
    
    // If it's already just a video ID (11 characters, alphanumeric with - and _)
    if (/^[a-zA-Z0-9_-]{11}$/.test(trimmed)) {
      return trimmed;
    }
    
    // Try to extract from various YouTube URL formats
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/v\/|youtube\.com\/shorts\/)([a-zA-Z0-9_-]{11})/,
      /^([a-zA-Z0-9_-]{11})$/  // Just the ID
    ];
    
    for (const pattern of patterns) {
      const match = trimmed.match(pattern);
      if (match && match[1]) {
        return match[1];
      }
    }
    
    // If no pattern matched, try to get anything that looks like a video ID from the URL
    const idMatch = trimmed.match(/[a-zA-Z0-9_-]{11}/);
    if (idMatch) {
      return idMatch[0];
    }
    
    return trimmed; // Return as-is if nothing worked
  };
  
  // Form data
  const [formData, setFormData] = useState({
    // Basic Info
    name: '',
    title: '',
    company: '',
    bio: '',
    // Photos
    profileImage: '',
    companyLogo: '',
    // Contact
    email: '',
    phone: '',
    phoneCountryCode: DEFAULT_COUNTRY_CODE,
    location: '',
    // Website (separate domain and extension)
    websiteName: '',
    websiteTLD: '.com',
  });
  
  // Social links as separate array (supports multiple of same type)
  const [socialLinks, setSocialLinks] = useState([]);
  const [showAddSocial, setShowAddSocial] = useState(false);

  const [imagePreview, setImagePreview] = useState('');
  const [logoPreview, setLogoPreview] = useState('');
  
  // Image cropping state
  const [showCropper, setShowCropper] = useState(false);
  const [imageToCrop, setImageToCrop] = useState(null);
  const [cropType, setCropType] = useState('photo'); // 'photo' or 'logo'
  
  const fileInputRef = useRef(null);
  const logoInputRef = useRef(null);
  const qrRef = useRef(null);
  
  // Refs to preserve data during sign-in (in case of any state issues)
  const formDataRef = useRef(formData);
  const selectedThemeRef = useRef(selectedTheme);
  const socialLinksRef = useRef(socialLinks);
  
  // Keep refs in sync with state
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);
  
  useEffect(() => {
    selectedThemeRef.current = selectedTheme;
  }, [selectedTheme]);
  
  useEffect(() => {
    socialLinksRef.current = socialLinks;
  }, [socialLinks]);

  // Load existing card data in edit mode
  useEffect(() => {
    const loadCardData = async () => {
      if (username && user) {
        setIsLoading(true);
        setIsEditMode(true);
        setExistingUsername(username);
        try {
          const idToken = await user.getIdToken();
          const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cards/${username}`, {
            headers: { 'Authorization': `Bearer ${idToken}` }
          });
          
          if (response.ok) {
            const card = await response.json();
            
            // Parse website URL if available
            let websiteName = card.websiteName || '';
            let websiteTLD = card.websiteTLD || '.com';
            
            // If no websiteName but has website URL, parse it
            if (!websiteName && card.website) {
              const cleanedUrl = card.website.replace(/^https?:\/\//, '').replace(/^www\./, '');
              const tldMatch = cleanedUrl.match(/(\.[a-z]{2,})/i);
              if (tldMatch) {
                websiteTLD = tldMatch[1];
                websiteName = cleanedUrl.replace(tldMatch[1], '').replace(/\/$/, '');
              } else {
                websiteName = cleanedUrl;
              }
            }
            
            // Parse phone number to extract country code and number separately
            const parsedPhone = parsePhoneNumber(card.phone);
            
            // Populate form data
            setFormData({
              name: card.name || '',
              title: card.title || '',
              company: card.company || '',
              bio: card.bio || card.richBio || '',
              profileImage: card.profileImage || '',
              companyLogo: card.companyLogo || '',
              email: card.email || '',
              phone: parsedPhone.number,
              phoneCountryCode: parsedPhone.countryCode,
              location: card.location || card.locationAddress || '',
              websiteName: websiteName,
              websiteTLD: websiteTLD,
            });
            
            // Set previews
            if (card.profileImage) setImagePreview(card.profileImage);
            if (card.companyLogo) setLogoPreview(card.companyLogo);
            
            // Set theme
            if (card.bioTemplate) setSelectedTheme(card.bioTemplate);
            
            // Convert old social fields to new array format
            const links = [];
            console.log('Loading social links from card:', { socialLinks: card.socialLinks, instagram: card.instagram, twitter: card.twitter });
            
            if (card.socialLinks && card.socialLinks.length > 0) {
              // Use new format if available, filter out website type as it's now separate
              const filteredLinks = card.socialLinks
                .filter(l => l.type !== 'website')
                .map(l => ({
                  type: l.type,
                  value: l.value || '',
                  label: l.label || '',
                  countryCode: l.countryCode || (l.type === 'whatsapp' ? DEFAULT_COUNTRY_CODE : undefined)
                }));
              links.push(...filteredLinks);
              console.log('Loaded socialLinks array:', filteredLinks);
            } else {
              // Convert old format - extract handles only
              if (card.linkedin) links.push({ type: 'linkedin', value: card.linkedin, label: '' });
              if (card.twitter) links.push({ type: 'twitter', value: card.twitter, label: '' });
              if (card.instagram) links.push({ type: 'instagram', value: card.instagram, label: '' });
              if (card.youtube) links.push({ type: 'youtube', value: card.youtube, label: '' });
              if (card.facebook) links.push({ type: 'facebook', value: card.facebook, label: '' });
              if (card.github) links.push({ type: 'github', value: card.github, label: '' });
              if (card.whatsapp) links.push({ type: 'whatsapp', value: card.whatsapp, label: '', countryCode: DEFAULT_COUNTRY_CODE });
              console.log('Converted from old format:', links);
            }
            setSocialLinks(links);
            
            // Set gallery
            if (card.galleryImages) setGalleryImages(card.galleryImages);
            if (card.youtubeVideos) setYoutubeLinks(card.youtubeVideos);
            
          } else {
            toast({ title: 'Card not found', variant: 'destructive' });
            navigate('/card-builder');
          }
        } catch (error) {
          console.error('Error loading card:', error);
          toast({ title: 'Error loading card', variant: 'destructive' });
        } finally {
          setIsLoading(false);
        }
      }
    };
    
    loadCardData();
  }, [username, user, navigate, toast]);

  // Save card function - used for both direct save and post-signin save
  const saveCard = async () => {
    if (!user) return;
    
    const currentFormData = formData.name ? formData : formDataRef.current;
    const currentTheme = selectedTheme || selectedThemeRef.current;
    const currentSocialLinks = socialLinks.length ? socialLinks : socialLinksRef.current;
    
    console.log('=== SAVE CARD DEBUG ===');
    console.log('isEditMode:', isEditMode);
    console.log('existingUsername:', existingUsername);
    console.log('socialLinks state:', socialLinks);
    console.log('currentSocialLinks:', currentSocialLinks);
    console.log('formData:', currentFormData);
    
    setIsSaving(true);
    try {
      const idToken = await user.getIdToken();
      
      const fullPhone = currentFormData.phone ? `${currentFormData.phoneCountryCode}${currentFormData.phone}` : '';
      
      // Process social links - get first of each type for backward compatibility
      const getSocialValue = (type) => {
        const link = currentSocialLinks.find(l => l.type === type);
        return link ? link.value : null;
      };
      
      // Get WhatsApp with proper formatting
      const whatsappLink = currentSocialLinks.find(l => l.type === 'whatsapp');
      const fullWhatsapp = whatsappLink ? (whatsappLink.countryCode || '') + whatsappLink.value : '';
      
      // Get website from social links
      const websiteLink = currentSocialLinks.find(l => l.type === 'website');
      
      const cardData = {
        name: currentFormData.name || 'My Card',
        userId: user.uid,
        title: currentFormData.title || null,
        company: currentFormData.company || null,
        email: currentFormData.email || user.email || 'user@pipercard.com',
        phone: fullPhone || null,
        // Website stored as domain name + TLD separately
        websiteName: currentFormData.websiteName || null,
        websiteTLD: currentFormData.websiteTLD || '.com',
        // Also construct full website URL for backward compatibility
        website: currentFormData.websiteName ? `https://${currentFormData.websiteName}${currentFormData.websiteTLD}` : null,
        location: currentFormData.location || null,
        bio: currentFormData.bio || null,
        richBio: currentFormData.bio || null, // Store rich bio content
        profileImage: currentFormData.profileImage || null,
        companyLogo: currentFormData.companyLogo || null,
        linkedin: getSocialValue('linkedin'),
        twitter: getSocialValue('twitter'),
        instagram: getSocialValue('instagram'),
        youtube: getSocialValue('youtube'),
        facebook: getSocialValue('facebook'),
        github: getSocialValue('github'),
        whatsapp: fullWhatsapp || null,
        bioTemplate: currentTheme,
        socialLinks: currentSocialLinks,
        galleryImages: galleryImages,
        youtubeVideos: youtubeLinks
      };

      console.log('=== CARD DATA TO SEND ===');
      console.log('cardData:', JSON.stringify(cardData, null, 2));
      console.log('socialLinks in cardData:', cardData.socialLinks);

      // Use PUT for edit mode, POST for new cards
      const apiUrl = isEditMode 
        ? `${process.env.REACT_APP_BACKEND_URL}/api/cards/${existingUsername}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/cards`;
      
      console.log('API URL:', apiUrl, 'Method:', isEditMode ? 'PUT' : 'POST');
      
      const response = await fetch(apiUrl, {
        method: isEditMode ? 'PUT' : 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify(cardData)
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Card saved successfully:', data);
        toast({ title: isEditMode ? 'Card updated successfully!' : 'Card created successfully!' });
        navigate(`/bio/${data.username || existingUsername}`);
      } else {
        const errorData = await response.json();
        console.error('Card save failed:', errorData);
        toast({ title: 'Error saving card', description: errorData.detail || 'Failed to save card', variant: 'destructive' });
      }
    } catch (error) {
      console.error('Card save error:', error);
      toast({ title: 'Error saving card', description: error.message, variant: 'destructive' });
    } finally {
      setIsSaving(false);
    }
  };

  // Effect to handle save after successful sign-in
  useEffect(() => {
    if (user && pendingSave) {
      console.log('User signed in, triggering save...');
      setPendingSave(false);
      saveCard();
    }
  }, [user, pendingSave]); // saveCard uses current state values directly

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleImageUpload = async (e, type) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      // Read file and show cropper
      const reader = new FileReader();
      reader.onload = (event) => {
        setImageToCrop(event.target.result);
        setCropType(type === 'profile' ? 'photo' : 'logo');
        setShowCropper(true);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Image upload error:', error);
      toast({ title: 'Error uploading image', description: error.message, variant: 'destructive' });
    }
  };

  const handleCropComplete = async (croppedImage) => {
    try {
      // Check if user is authenticated for Firebase upload
      if (!user) {
        toast({ title: 'Please sign in to upload images', variant: 'destructive' });
        return;
      }
      
      toast({
        title: "Uploading image...",
        description: "Please wait while we upload your image."
      });
      
      // Convert blob URL to blob, then to file
      const response = await fetch(croppedImage);
      const blob = await response.blob();
      const file = new File([blob], 'cropped-image.jpg', { type: 'image/jpeg' });
      
      // Upload to Firebase Storage
      const storagePath = `${cropType === 'photo' ? 'profile-images' : 'company-logos'}/${user.uid}-${Date.now()}.jpg`;
      const firebaseUrl = await firebaseUploadImage(file, storagePath);
      
      if (cropType === 'photo') {
        setFormData(prev => ({ ...prev, profileImage: firebaseUrl }));
        setImagePreview(firebaseUrl);
      } else {
        setFormData(prev => ({ ...prev, companyLogo: firebaseUrl }));
        setLogoPreview(firebaseUrl);
      }
      
      // Revoke the blob URL to free memory
      URL.revokeObjectURL(croppedImage);
      setImageToCrop(null);
      
      toast({
        title: `${cropType === 'photo' ? 'Photo' : 'Logo'} uploaded successfully`,
        description: "Your image has been saved."
      });
    } catch (error) {
      console.error('Crop/upload error:', error);
      toast({ title: 'Error uploading image', description: error.message, variant: 'destructive' });
    }
  };

  const nextStep = () => {
    // Validate name is required on step 1
    if (currentStep === 1 && !formData.name.trim()) {
      toast({ 
        title: 'Name is required', 
        description: 'Please enter your name to continue',
        variant: 'destructive' 
      });
      return;
    }
    
    // Validate email is required on step 4 (Contact)
    if (currentStep === 4 && !formData.email.trim()) {
      toast({ 
        title: 'Email is required', 
        description: 'Please enter your email address',
        variant: 'destructive' 
      });
      return;
    }
    
    if (currentStep < 6) {
      setCurrentStep(prev => prev + 1);
    } else {
      setShowPreview(true);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSave = async () => {
    if (!user) {
      setPendingSave(true); // Mark that we want to save after sign-in
      setShowSignInModal(true);
      return;
    }
    
    await saveCard();
  };

  const handleShare = () => {
    if (!user) {
      setPendingSave(true);
      setShowSignInModal(true);
      return;
    }
    // Share functionality
    if (navigator.share) {
      navigator.share({
        title: `${formData.name}'s Card`,
        text: 'Check out my digital business card',
        url: window.location.href
      });
    }
  };

  const handleDownloadQR = () => {
    if (!user) {
      setShowSignInModal(true);
      return;
    }
    // Download QR using the QRCodeWithLogo component's canvas
    toast({ title: 'QR Code will be available after saving your card' });
  };

  // Swipe gesture handlers
  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchEndX(null);
    setTouchStart(e.targetTouches[0].clientY);
    setTouchStartX(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientY);
    setTouchEndX(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd || !touchStartX || !touchEndX) return;
    
    const distanceY = touchStart - touchEnd;
    const distanceX = touchStartX - touchEndX;
    
    // Determine if horizontal or vertical swipe is more prominent
    const isHorizontalSwipe = Math.abs(distanceX) > Math.abs(distanceY);
    
    if (isHorizontalSwipe) {
      // Horizontal swipe - change steps
      const isSwipeLeft = distanceX > minSwipeDistance;
      const isSwipeRight = distanceX < -minSwipeDistance;
      
      if (isSwipeLeft && !showPreview && currentStep < 6) {
        // Swipe left to go to next step
        nextStep();
      } else if (isSwipeRight && !showPreview && currentStep > 1) {
        // Swipe right to go to previous step
        prevStep();
      }
    } else {
      // Vertical swipe - show/hide preview
      const isSwipeUp = distanceY > minSwipeDistance;
      const isSwipeDown = distanceY < -minSwipeDistance;
      
      if (isSwipeUp && !showPreview) {
        // Swipe up to open preview
        setShowPreview(true);
      } else if (isSwipeDown && showPreview) {
        // Swipe down to close preview
        setShowPreview(false);
      }
    }
  };

  const progress = (currentStep / 6) * 100;

  // Render step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Full Name <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder="John Doe"
                  className="pl-10 h-12 text-base"
                  data-testid="input-name"
                  autoFocus
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Job Title
              </label>
              <div className="relative">
                <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  placeholder="Software Engineer"
                  className="pl-10 h-12 text-base"
                  data-testid="input-title"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Company
              </label>
              <div className="relative">
                <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  value={formData.company}
                  onChange={(e) => handleInputChange('company', e.target.value)}
                  placeholder="Acme Inc"
                  className="pl-10 h-12 text-base"
                  data-testid="input-company"
                />
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                About You
              </label>
              <p className="text-xs text-gray-500 mb-3">
                Write a compelling bio about yourself. Use formatting to highlight your experience, skills, and achievements.
              </p>
              <div className="border border-gray-200 rounded-xl overflow-hidden">
                <RichTextEditor
                  value={formData.bio}
                  onChange={(value) => handleInputChange('bio', value)}
                  placeholder="Tell people about yourself... Use headings, lists, and formatting to make your bio stand out."
                />
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            {/* Profile Photo */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Profile Photo
              </label>
              <div className="flex items-center gap-4">
                <div 
                  onClick={() => fileInputRef.current?.click()}
                  className="w-24 h-24 sm:w-28 sm:h-28 rounded-full bg-gray-100 border-2 border-dashed border-gray-300 flex items-center justify-center cursor-pointer hover:border-amber-500 hover:bg-amber-50 transition-all overflow-hidden"
                >
                  {imagePreview ? (
                    <img src={imagePreview} alt="Profile" className="w-full h-full object-cover" />
                  ) : (
                    <div className="text-center">
                      <Camera className="w-8 h-8 text-gray-400 mx-auto" />
                      <span className="text-xs text-gray-500 mt-1">Add Photo</span>
                    </div>
                  )}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleImageUpload(e, 'profile')}
                  className="hidden"
                />
                <div className="flex-1">
                  <p className="text-sm text-gray-600 mb-2">Upload a professional photo</p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    className="text-sm"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Choose File
                  </Button>
                </div>
              </div>
            </div>

            {/* Company Logo */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Company Logo (Optional)
              </label>
              <div className="flex items-center gap-4">
                <div 
                  onClick={() => logoInputRef.current?.click()}
                  className="w-20 h-20 sm:w-24 sm:h-24 rounded-xl bg-gray-100 border-2 border-dashed border-gray-300 flex items-center justify-center cursor-pointer hover:border-amber-500 hover:bg-amber-50 transition-all overflow-hidden"
                >
                  {logoPreview ? (
                    <img src={logoPreview} alt="Logo" className="w-full h-full object-contain p-2" />
                  ) : (
                    <div className="text-center">
                      <Building className="w-6 h-6 text-gray-400 mx-auto" />
                      <span className="text-[10px] text-gray-500 mt-1">Add Logo</span>
                    </div>
                  )}
                </div>
                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleImageUpload(e, 'logo')}
                  className="hidden"
                />
                <div className="flex-1">
                  <p className="text-sm text-gray-600 mb-2">Add your company logo</p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => logoInputRef.current?.click()}
                    className="text-sm"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Choose File
                  </Button>
                </div>
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Email Address <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder="john@example.com"
                  className="pl-10 h-12 text-base"
                  data-testid="input-email"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Phone Number
              </label>
              <div className="flex gap-2">
                <CountryCodeSelect
                  value={formData.phoneCountryCode}
                  onChange={(code) => handleInputChange('phoneCountryCode', code)}
                  className="w-24"
                />
                <div className="relative flex-1">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    placeholder="9876543210"
                    className="pl-10 h-12 text-base"
                    data-testid="input-phone"
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Website
              </label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    value={formData.websiteName}
                    onChange={(e) => {
                      // Clean input: remove protocol, www, and any TLD
                      let value = e.target.value
                        .replace(/^https?:\/\//, '')
                        .replace(/^www\./, '')
                        .replace(/\.[a-z]{2,}.*$/i, ''); // Remove TLD and anything after
                      handleInputChange('websiteName', value);
                    }}
                    placeholder="yourwebsite"
                    className="pl-10 h-12 text-base"
                    data-testid="input-website"
                  />
                </div>
                <select
                  value={formData.websiteTLD}
                  onChange={(e) => handleInputChange('websiteTLD', e.target.value)}
                  className="h-12 px-3 border border-gray-200 rounded-lg bg-white text-gray-700 focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                >
                  {DOMAIN_EXTENSIONS.map(ext => (
                    <option key={ext} value={ext}>{ext}</option>
                  ))}
                </select>
              </div>
              {formData.websiteName && (
                <p className="text-xs text-gray-500 mt-1">
                  Your website: <span className="text-amber-600 font-medium">https://{formData.websiteName}{formData.websiteTLD}</span>
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Location
              </label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  value={formData.location}
                  onChange={(e) => handleInputChange('location', e.target.value)}
                  placeholder="Mumbai, India"
                  className="pl-10 h-12 text-base"
                  data-testid="input-location"
                />
              </div>
            </div>
          </div>
        );

      case 5:
        return (
          <div className="space-y-4">
            {/* Added Social Links */}
            {socialLinks.length > 0 && (
              <div className="space-y-3">
                {socialLinks.map((link, index) => {
                  const platform = SOCIAL_PLATFORMS.find(p => p.id === link.type);
                  const IconComponent = platform?.icon || Globe;
                  
                  return (
                    <div key={index} className="relative bg-gray-50 rounded-xl p-3 border border-gray-200">
                      <div className="flex items-center gap-3">
                        <div 
                          className="w-10 h-10 rounded-full flex items-center justify-center"
                          style={{ backgroundColor: `${platform?.color}15` }}
                        >
                          <IconComponent className="w-5 h-5" style={{ color: platform?.color }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-sm font-medium text-gray-700">{platform?.name || link.type}</span>
                            {link.label && (
                              <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">{link.label}</span>
                            )}
                          </div>
                          <Input
                            value={link.value}
                            onChange={(e) => {
                              const newLinks = [...socialLinks];
                              // Extract handle from URL if user pastes a full URL
                              const extractedValue = extractHandle(e.target.value, platform);
                              newLinks[index].value = extractedValue;
                              setSocialLinks(newLinks);
                            }}
                            onBlur={(e) => {
                              // Also clean on blur in case onChange didn't catch it
                              const newLinks = [...socialLinks];
                              newLinks[index].value = extractHandle(e.target.value, platform);
                              setSocialLinks(newLinks);
                            }}
                            placeholder={platform?.placeholder || 'username'}
                            className="h-10 text-sm"
                          />
                          {link.value && platform?.id !== 'whatsapp' && (
                            <p className="text-xs text-gray-400 mt-1">
                              Handle: <span className="text-amber-600">@{link.value}</span>
                            </p>
                          )}
                          {link.type === 'whatsapp' && (
                            <div className="mt-2">
                              <CountryCodeSelect
                                value={link.countryCode || DEFAULT_COUNTRY_CODE}
                                onChange={(code) => {
                                  const newLinks = [...socialLinks];
                                  newLinks[index].countryCode = code;
                                  setSocialLinks(newLinks);
                                }}
                                className="w-full"
                              />
                            </div>
                          )}
                          <Input
                            value={link.label || ''}
                            onChange={(e) => {
                              const newLinks = [...socialLinks];
                              newLinks[index].label = e.target.value;
                              setSocialLinks(newLinks);
                            }}
                            placeholder="Label (optional, e.g., Personal, Work)"
                            className="h-9 text-xs mt-2 bg-white"
                          />
                        </div>
                        <button
                          onClick={() => {
                            setSocialLinks(socialLinks.filter((_, i) => i !== index));
                          }}
                          className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Add Social Link Button */}
            {!showAddSocial ? (
              <button
                onClick={() => setShowAddSocial(true)}
                className="w-full py-4 border-2 border-dashed border-gray-300 rounded-xl text-gray-500 hover:border-amber-400 hover:text-amber-600 hover:bg-amber-50 transition-all flex items-center justify-center gap-2"
              >
                <Plus className="w-5 h-5" />
                Add Social Link
              </button>
            ) : (
              <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
                <p className="text-sm font-medium text-gray-700 mb-3">Select a platform:</p>
                <div className="grid grid-cols-4 gap-2">
                  {SOCIAL_PLATFORMS.map((platform) => {
                    const IconComponent = platform.icon;
                    return (
                      <button
                        key={platform.id}
                        onClick={() => {
                          setSocialLinks([...socialLinks, { 
                            type: platform.id, 
                            value: '', 
                            label: '',
                            countryCode: platform.id === 'whatsapp' ? DEFAULT_COUNTRY_CODE : undefined
                          }]);
                          setShowAddSocial(false);
                        }}
                        className="flex flex-col items-center gap-1 p-3 rounded-lg hover:bg-white transition-colors border border-transparent hover:border-gray-200"
                      >
                        <div 
                          className="w-10 h-10 rounded-full flex items-center justify-center"
                          style={{ backgroundColor: `${platform.color}15` }}
                        >
                          <IconComponent className="w-5 h-5" style={{ color: platform.color }} />
                        </div>
                        <span className="text-xs text-gray-600 truncate w-full text-center">{platform.name}</span>
                      </button>
                    );
                  })}
                </div>
                <button
                  onClick={() => setShowAddSocial(false)}
                  className="w-full mt-3 text-sm text-gray-500 hover:text-gray-700"
                >
                  Cancel
                </button>
              </div>
            )}

            {socialLinks.length === 0 && !showAddSocial && (
              <p className="text-center text-sm text-gray-500 py-2">
                Add your social profiles to make it easy for people to connect with you
              </p>
            )}
          </div>
        );

      case 6:
        return (
          <div className="space-y-6">
            {/* Gallery Images */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Gallery Images (up to 4)
              </label>
              <p className="text-xs text-gray-500 mb-3">Add photos to showcase your work, portfolio, or achievements</p>
              
              <div className="grid grid-cols-2 gap-3">
                {galleryImages.map((img, index) => (
                  <div key={index} className="relative aspect-square rounded-xl overflow-hidden bg-gray-100 border-2 border-gray-200">
                    <img src={img} alt={`Gallery ${index + 1}`} className="w-full h-full object-cover" />
                    <button
                      onClick={() => setGalleryImages(galleryImages.filter((_, i) => i !== index))}
                      className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                
                {galleryImages.length < 4 && (
                  <button
                    onClick={() => galleryInputRef.current?.click()}
                    className="aspect-square rounded-xl border-2 border-dashed border-gray-300 flex flex-col items-center justify-center gap-2 hover:border-amber-400 hover:bg-amber-50 transition-all"
                  >
                    <Plus className="w-8 h-8 text-gray-400" />
                    <span className="text-sm text-gray-500">Add Image</span>
                  </button>
                )}
              </div>
              
              <input
                ref={galleryInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (file && galleryImages.length < 4) {
                    try {
                      // compressImage returns base64 string directly
                      const compressedBase64 = await compressImage(file);
                      setGalleryImages([...galleryImages, compressedBase64]);
                    } catch (error) {
                      console.error('Image compression error:', error);
                      toast({ title: 'Error processing image', description: error.message, variant: 'destructive' });
                    }
                  }
                  // Reset input so same file can be selected again
                  e.target.value = '';
                }}
              />
            </div>

            {/* YouTube Links */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                YouTube Videos (up to 4)
              </label>
              <p className="text-xs text-gray-500 mb-3">Add YouTube video links to embed on your bio page</p>
              
              <div className="space-y-3">
                {youtubeLinks.map((link, index) => (
                  <div key={index} className="flex flex-col gap-1">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 relative">
                        <Youtube className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-red-500" />
                        <Input
                          value={link}
                          onChange={(e) => {
                            const newLinks = [...youtubeLinks];
                            // Extract video ID from URL
                            newLinks[index] = extractYouTubeId(e.target.value);
                            setYoutubeLinks(newLinks);
                          }}
                          placeholder="Paste YouTube URL or video ID"
                          className="pl-10 h-11"
                        />
                      </div>
                      <button
                        onClick={() => setYoutubeLinks(youtubeLinks.filter((_, i) => i !== index))}
                        className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                    {link && (
                      <p className="text-xs text-gray-500 ml-10">
                        Video ID: <span className="text-red-600 font-mono">{link}</span>
                      </p>
                    )}
                  </div>
                ))}
                
                {youtubeLinks.length < 4 && (
                  <button
                    onClick={() => setYoutubeLinks([...youtubeLinks, ''])}
                    className="w-full py-3 border-2 border-dashed border-gray-300 rounded-xl text-gray-500 hover:border-red-400 hover:text-red-600 hover:bg-red-50 transition-all flex items-center justify-center gap-2"
                  >
                    <Plus className="w-5 h-5" />
                    Add YouTube Video
                  </button>
                )}
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // Dummy functions for preview (no actual tracking)
  const noopTrackLinkClick = () => {};
  const constructSocialUrl = (type, value) => {
    const baseUrls = {
      linkedin: 'https://linkedin.com/in/',
      twitter: 'https://twitter.com/',
      instagram: 'https://instagram.com/',
      youtube: 'https://youtube.com/',
      facebook: 'https://facebook.com/',
      github: 'https://github.com/',
      whatsapp: 'https://wa.me/'
    };
    return `${baseUrls[type] || ''}${value}`;
  };

  // Build complete card object for live preview (includes social links, gallery, videos)
  const buildLivePreviewCard = () => {
    const fullPhone = formData.phone ? `${formData.phoneCountryCode}${formData.phone}` : '';
    
    // Get social values from socialLinks array
    const getSocialValue = (type) => {
      const link = socialLinks.find(l => l.type === type);
      return link ? link.value : '';
    };
    
    const whatsappLink = socialLinks.find(l => l.type === 'whatsapp');
    const fullWhatsapp = whatsappLink ? (whatsappLink.countryCode || '') + whatsappLink.value : '';
    
    return {
      name: formData.name || 'Your Name',
      title: formData.title || 'Your Title',
      company: formData.company || '',
      email: formData.email || '',
      phone: fullPhone,
      website: formData.websiteName ? `https://${formData.websiteName}${formData.websiteTLD}` : '',
      location: formData.location || '',
      locationAddress: formData.location || '',
      bio: formData.bio || '',
      richBio: formData.bio || '',
      profileImage: imagePreview || '',
      companyLogo: logoPreview || '/piper-logo-qr.png',
      linkedin: getSocialValue('linkedin'),
      twitter: getSocialValue('twitter'),
      instagram: getSocialValue('instagram'),
      youtube: getSocialValue('youtube'),
      facebook: getSocialValue('facebook'),
      github: getSocialValue('github'),
      whatsapp: fullWhatsapp,
      bioTemplate: selectedTheme,
      // Include social links for template rendering
      socialLinks: socialLinks.filter(l => l.value),
      // Include gallery and videos
      galleryImages: galleryImages,
      youtubeVideos: youtubeLinks.filter(l => l)
    };
  };

  return (
    <Layout>
      <SEO 
        title={isEditMode ? "Edit Your Card | Piper Card" : "Create Your Digital Business Card | Piper Card"}
        description="Create your professional digital business card in minutes. Free, easy, and shareable."
      />

      <div 
        className="min-h-screen bg-gray-50"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {/* Loading state */}
        {isLoading && (
          <div className="fixed inset-0 bg-white/80 z-50 flex items-center justify-center">
            <div className="text-center">
              <div className="w-12 h-12 border-4 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-gray-600">Loading card data...</p>
            </div>
          </div>
        )}

        {/* Main Content - Full Width on Desktop */}
        <div className="max-w-7xl mx-auto px-4 py-6">
          {/* Page Header - similar to other tools */}
          <div className="bg-white border-b border-gray-200 sticky top-0 z-40 -mx-4 px-4 py-3 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CreditCard className="w-6 h-6 text-amber-500" />
                <span className="font-bold text-lg text-gray-900">
                  {isEditMode ? 'Edit Card' : 'Card Builder'}
                </span>
              </div>
              <button
                onClick={() => setShowPreview(true)}
                className="lg:hidden text-amber-600 font-medium text-sm flex items-center gap-1 hover:text-amber-700"
                data-testid="preview-button"
              >
                <Eye className="w-4 h-4" />
                Preview
              </button>
            </div>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-10">
            {/* Left Column: Steps Form */}
            <div>
              {/* Progress Steps */}
              <div className="bg-white border border-gray-200 rounded-xl p-4 mb-6">
                <div className="flex items-center justify-between overflow-x-auto pb-1">
                  {STEPS.map((step, index) => {
                    const StepIcon = step.icon;
                    const isActive = currentStep === step.id;
                    const isCompleted = currentStep > step.id;
                    
                    return (
                      <div key={step.id} className="flex items-center flex-shrink-0">
                        <button
                          onClick={() => setCurrentStep(step.id)}
                          className={`flex items-center gap-1.5 ${currentStep >= step.id ? 'text-amber-600' : 'text-gray-400'}`}
                        >
                          <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                            isCompleted ? 'bg-amber-500 text-white' :
                            isActive ? 'bg-amber-100 text-amber-600 border-2 border-amber-500' :
                            'bg-gray-100 text-gray-400'
                          }`}>
                            {isCompleted ? <Check className="w-3.5 h-3.5" /> : <StepIcon className="w-3.5 h-3.5" />}
                          </div>
                          <span className="text-[10px] sm:text-xs font-medium hidden sm:inline whitespace-nowrap">{step.title}</span>
                        </button>
                        {index < STEPS.length - 1 && (
                          <div className={`w-3 sm:w-6 h-0.5 mx-1 ${currentStep > step.id ? 'bg-amber-500' : 'bg-gray-200'}`} />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Step Content */}
              <div className="bg-white border border-gray-200 rounded-xl p-5 mb-6">
                <div className="mb-4">
                  <h2 className="text-xl font-bold text-gray-900">{STEPS[currentStep - 1].title}</h2>
                  <p className="text-sm text-gray-500 mt-1">
                    {currentStep === 1 && "Let's start with your basic information"}
                    {currentStep === 2 && "Tell people about yourself with rich formatting"}
                    {currentStep === 3 && "Add a photo to make your card personal"}
                    {currentStep === 4 && "How can people reach you?"}
                    {currentStep === 5 && "Connect your social profiles"}
                    {currentStep === 6 && "Showcase your work with images and videos"}
                  </p>
                </div>
                {renderStepContent()}
              </div>

              {/* Navigation Buttons */}
              <div className="flex items-center justify-between gap-4 mb-6 lg:mb-0">
                {currentStep > 1 ? (
                  <Button variant="outline" onClick={prevStep} className="flex-1 h-12" data-testid="back-button">
                    <ChevronLeft className="w-5 h-5 mr-1" /> Back
                  </Button>
                ) : (
                  <div className="flex-1" />
                )}
                
                {currentStep === 6 ? (
                  isEditMode ? (
                    <Button
                      onClick={saveCard}
                      disabled={isSaving}
                      className="flex-1 h-12 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white"
                      data-testid="save-button"
                    >
                      {isSaving ? 'Saving...' : <><Save className="w-5 h-5 mr-2" /> Save Changes</>}
                    </Button>
                  ) : (
                    <Button
                      onClick={() => setShowPreview(true)}
                      className="flex-1 h-12 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white lg:hidden"
                      data-testid="preview-create-button"
                    >
                      <Eye className="w-5 h-5 mr-2" /> Preview & Create
                    </Button>
                  )
                ) : (
                  <Button
                    onClick={nextStep}
                    className="flex-1 h-12 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white"
                    data-testid="next-button"
                  >
                    Next <ChevronRight className="w-5 h-5 ml-1" />
                  </Button>
                )}
              </div>

              {/* Mobile Swipe-Up Preview Indicator */}
              <div 
                className="lg:hidden mt-6 mb-4 flex flex-col items-center cursor-pointer"
                onClick={() => setShowPreview(true)}
                data-testid="swipe-up-indicator"
              >
                <div className="flex flex-col items-center animate-bounce">
                  <ChevronUp className="w-6 h-6 text-amber-500" />
                  <ChevronUp className="w-6 h-6 text-amber-500 -mt-4" />
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <Eye className="w-4 h-4 text-amber-600" />
                  <span className="text-sm font-medium text-amber-600">Swipe Up to Preview</span>
                </div>
                <p className="text-xs text-gray-400 mt-1">Swipe left/right to change steps</p>
              </div>
            </div>

            {/* Right Column: Live Preview (Desktop Only) */}
            <div className="hidden lg:block">
              <div className="sticky top-24">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Eye className="w-5 h-5 text-amber-500" />
                  Live Preview
                </h3>
                
                {/* Preview Container */}
                <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                  <div className="max-h-[550px] overflow-y-auto">
                    <BioTemplateRenderer 
                      card={buildLivePreviewCard()}
                      bioUrl="https://pipercard.com/preview"
                      constructSocialUrl={constructSocialUrl}
                      trackLinkClick={noopTrackLinkClick}
                      hideQR={true}
                      isPremium={false}
                    />
                  </div>
                  
                  {/* Action Button */}
                  <div className="p-4 border-t border-gray-200">
                    {isEditMode ? (
                      <Button
                        onClick={saveCard}
                        disabled={isSaving}
                        className="w-full h-12 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white"
                      >
                        {isSaving ? 'Saving...' : <><Save className="w-5 h-5 mr-2" /> Save Changes</>}
                      </Button>
                    ) : (
                      <Button
                        onClick={() => {
                          if (!user) {
                            setShowSignInModal(true);
                          } else {
                            saveCard();
                          }
                        }}
                        disabled={isSaving || !formData.name}
                        className="w-full h-12 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white"
                      >
                        {isSaving ? 'Creating...' : user ? <><Save className="w-5 h-5 mr-2" /> Create Card</> : <><User className="w-5 h-5 mr-2" /> Sign In to Create</>}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Preview Modal */}
        <Dialog open={showPreview} onOpenChange={setShowPreview}>
          <DialogContent 
            className="w-[95vw] max-w-2xl max-h-[90vh] overflow-y-auto p-0"
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >
            {/* Swipe down indicator */}
            <div className="flex justify-center pt-2 pb-1">
              <div className="w-10 h-1 bg-gray-300 rounded-full" />
            </div>
            <p className="text-xs text-center text-gray-400 mb-2">Swipe down to close</p>
            
            <DialogHeader className="px-4 pb-3 border-b sticky top-0 bg-white z-10">
              <DialogTitle className="text-lg font-semibold text-center">Your Card Preview</DialogTitle>
            </DialogHeader>

            {/* Real Bio Template Preview - with gallery, videos, links */}
            <div className="relative overflow-hidden rounded-lg" style={{ transform: 'scale(0.85)', transformOrigin: 'top center', maxHeight: '60vh' }}>
              <BioTemplateRenderer
                card={buildLivePreviewCard()}
                bioUrl="https://pipercard.com/preview"
                constructSocialUrl={constructSocialUrl}
                trackLinkClick={noopTrackLinkClick}
                hideQR={true}
                isOwner={false}
                isPremium={false}
              />
            </div>

            {/* QR Code with Logo */}
            <div className="flex justify-center py-4 bg-gray-50">
              <div className="bg-white p-3 rounded-xl shadow-md">
                <QRCodeWithLogo
                  value="https://pipercard.com/preview"
                  size={120}
                  showLogo={true}
                  logoUrl={logoPreview || '/piper-logo-qr.png'}
                  qrColor="#d97706"
                />
                <p className="text-xs text-center text-gray-500 mt-2">Scan to preview</p>
              </div>
            </div>

            {/* Theme Selection */}
            <div className="px-4 pb-4">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <Palette className="w-4 h-4" />
                  Choose Theme
                </span>
              </div>
              <div className="grid grid-cols-4 gap-2">
                {THEMES.map((theme) => (
                  <button
                    key={theme.id}
                    onClick={() => setSelectedTheme(theme.id)}
                    className={`relative p-2 rounded-lg border-2 transition-all ${
                      selectedTheme === theme.id
                        ? 'border-amber-500 ring-2 ring-amber-200'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    title={theme.name}
                  >
                    <div className={`w-full h-8 rounded ${theme.preview}`} />
                    <span className="text-[10px] text-gray-600 mt-1 block truncate">{theme.name}</span>
                    {selectedTheme === theme.id && (
                      <div className="absolute -top-1 -right-1 w-4 h-4 bg-amber-500 rounded-full flex items-center justify-center">
                        <Check className="w-3 h-3 text-white" />
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3 p-4 border-t bg-white sticky bottom-0">
              <Button
                onClick={handleSave}
                disabled={isSaving || !formData.name}
                className="w-full h-12 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white text-base font-semibold"
              >
                {isSaving ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Saving...
                  </span>
                ) : (
                  <>
                    <Save className="w-5 h-5 mr-2" />
                    {user ? 'Save Card' : 'Sign in to Save'}
                  </>
                )}
              </Button>

              <div className="grid grid-cols-2 gap-3">
                <Button
                  variant="outline"
                  onClick={handleShare}
                  className="h-11"
                >
                  <Share2 className="w-4 h-4 mr-2" />
                  Share
                </Button>
                <Button
                  variant="outline"
                  onClick={handleDownloadQR}
                  className="h-11"
                >
                  <Download className="w-4 h-4 mr-2" />
                  QR Code
                </Button>
              </div>

              <button
                onClick={() => setShowPreview(false)}
                className="w-full text-center text-sm text-gray-500 mt-2 hover:text-gray-700"
              >
                ← Back to editing
              </button>
            </div>

            {!user && (
              <p className="text-xs text-center text-gray-500 mt-4 bg-amber-50 p-3 rounded-lg">
                Sign in to save your card and get a shareable link
              </p>
            )}
          </DialogContent>
        </Dialog>

        {/* Sign In Modal */}
        <SignInModal 
          open={showSignInModal} 
          onOpenChange={setShowSignInModal}
          noRedirect={true}
        />

        {/* Image Cropper Modal */}
        <ImageCropper
          open={showCropper}
          onClose={() => {
            setShowCropper(false);
            setImageToCrop(null);
          }}
          imageSrc={imageToCrop}
          onCropComplete={handleCropComplete}
          aspectRatio={1}
          cropShape="rect"
          title={cropType === 'photo' ? 'Crop Your Photo' : 'Crop Your Logo'}
        />
      </div>
    </Layout>
  );
};

export default CardBuilder;
